import { sendGAEvent } from '@next/third-parties/google';
import * as Sentry from '@sentry/nextjs';
import { useMemoizedFn, useThrottleFn } from 'ahooks';
import { useTranslations } from 'next-intl';
import { Children, type FC, type ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { BaseLineCreationCheck } from '@/app/components/icons/BaseLineCreationCheck';
import { BaseLineSubmit3 } from '@/app/components/icons/BaseLineSubmit3';
import { Fail } from '@/app/components/icons/Fail';
import type { ActionDialogProps } from '@/app/components/shared/ActionDialog';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { ImageApplyButton } from '@/app/components/shared/ImageApplyButton';
import { ImageWithPlaceholder } from '@/app/components/shared/ImageWithPlaceholder';
import { MediaWithWatermark } from '@/app/components/shared/MediaWithWatermark';
import { VideoApplyButton } from '@/app/components/shared/VideoApplyButton';
import { VideoPlayer } from '@/app/components/shared/VideoPlayer';
import { Button } from '@/app/components/ui/button';
import { SITE } from '@/app/constants';
import { useDevice, useGlobalModal, useIsScreen } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { VideoPlayerErrorInfo } from '@/app/types';
import { type CreationInfo, type CreationWithTask, EllipsisActionTypeEnum, type TaskInfo } from '@/app/types';
import { checkUrlAvailable, cn, getReportParams, getResourceUrl, reportEvent } from '@/app/utils';
import { isHevcPlaybackErrorCode, isHevcSupported, markHevcNotSupported } from '@/app/utils/client';
import { useLocale } from '@/i18n/routing';
import { FINISHED_STATES, THIRD_PARTY_AUDIT_ILLEGAL_ERROR_CODE } from '../constants';
import { ActionButtonType, type DisplayTaskState, type TaskStateInfo } from '../types';
import { getEstimateTime, getQueueI18nMessageConfig } from '../utils';
import { getImageButtonConfig, getVideoButtonConfig } from '../utils/button-config';
import { CancelButton } from './CancelButton';
import { CreationActionButtons } from './CreationActionButtons';
import { CustomProgress } from './CustomProgress';
import { TaskStatusAnimation } from './TaskStatusAnimation';

interface VisualCreationCardProps {
  creation?: CreationInfo;
  creationsLength: number;
  task: TaskInfo;
  taskState: DisplayTaskState;
  queueWaitTime?: TaskStateInfo['queue_wait_time'];
  isWaitTimeProlonged: boolean;
  onOpenActionDialog: (dialogAction: ActionDialogProps) => void;
  onOpenDetail: (creation: CreationWithTask) => void;
  onCancelTask?: (id: string) => void;
  onSubmit?: (creation: CreationInfo) => void;
}

// CreationPolicyViolation 机审审核不过，failed 人审失败
const AUDIT_FAILED_ERROR_CODES = new Set(['CreationPolicyViolation', 'failed', 'AuditSubmitIllegal']);

const isAuditFailedErrorCode = (errorCode?: string) =>
  errorCode !== undefined && AUDIT_FAILED_ERROR_CODES.has(errorCode);

const UpgradeGradientContent: FC<{ children: ReactNode }> = ({ children }) => {
  const id = useId().replace(/:/g, '');
  const textRef = useRef<SVGTextElement>(null);
  const [textWidth, setTextWidth] = useState(0);
  const label = Children.toArray(children)
    .filter((child): child is string | number => typeof child === 'string' || typeof child === 'number')
    .join('');

  useLayoutEffect(() => {
    setTextWidth(textRef.current?.getComputedTextLength() ?? 0);
  }, [label]);

  const measuredWidth = Math.ceil(textWidth);
  const totalWidth = Math.max(20, measuredWidth + 1);
  const gradientId = `task-upgrade-gradient-${id}`;
  const maskId = `task-upgrade-mask-${id}`;

  return (
    <>
      <svg
        aria-hidden="true"
        className="block h-5 overflow-visible"
        width={totalWidth}
        height="20"
        viewBox={`0 0 ${totalWidth} 20`}
      >
        <defs>
          <linearGradient
            id={gradientId}
            gradientUnits="userSpaceOnUse"
            x1="0"
            y1="0"
            x2={totalWidth}
            y2="0"
            spreadMethod="repeat"
          >
            <stop offset="0" stopColor="#5765f2" />
            <stop offset="0.42" stopColor="#babfff" />
            <stop offset="1" stopColor="#5765f2" />
            <animate attributeName="x1" values={`0;${totalWidth}`} dur="6s" repeatCount="indefinite" />
            <animate attributeName="x2" values={`${totalWidth};${totalWidth * 2}`} dur="6s" repeatCount="indefinite" />
          </linearGradient>
          <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width={totalWidth} height="20">
            <text ref={textRef} x="0" y="15" fill="white" className="text-sm" style={{ fontFamily: 'inherit' }}>
              {label}
            </text>
          </mask>
        </defs>
        <rect width={totalWidth} height="20" fill={`url(#${gradientId})`} mask={`url(#${maskId})`} />
      </svg>
      <span className="sr-only">{children}</span>
    </>
  );
};

export const VisualCreationCard: FC<VisualCreationCardProps> = ({
  creation,
  creationsLength,
  task,
  taskState,
  queueWaitTime,
  isWaitTimeProlonged,
  onOpenActionDialog,
  onOpenDetail,
  onCancelTask,
  onSubmit,
}) => {
  const t = useTranslations();
  const locale = useLocale();
  const { isMobile } = useDevice();
  const { isLgScreen } = useIsScreen();
  const { open: openPlanDetailDialog } = useGlobalModal('planDetailDialog');
  const { open: openDeleteDialog } = useGlobalModal('deleteDialog');

  const { userInfo, updateTask, removeTask, sceneFormParams, isTeamSpace } = useStore((state) => ({
    userInfo: state.userInfo,
    updateTask: state.updateTask,
    removeTask: state.removeTask,
    sceneFormParams: state.sceneFormParams,
    isTeamSpace: state.isTeamSpace,
  }));
  const [showActions, setShowActions] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showHevcMismatchTip, setShowHevcMismatchTip] = useState(false);
  const lastVideoErrorKeyRef = useRef<string>('');

  const { run: throttleSetShowActions } = useThrottleFn((show: boolean) => setShowActions(show), {
    wait: 100,
  });

  const { settings, type, id: taskId } = task;
  const creationId = creation?.id || '';
  const reportParams = { ...getReportParams(type, settings, task.scene), interaction_position: `${type}_task_card` };
  const hasFinished = FINISHED_STATES.includes(taskState);
  const isStarred = creation?.is_favor || false;
  const taskHistoryContainer = document.getElementById('taskHistoryContainer');
  const creationCardCount = settings?.sample_count ?? 1;
  const showPostButton = creationCardCount > 1 && !isTeamSpace && task.extra_params?.is_my_voice !== true;
  const continueCreationTextPrompt =
    task.input.prompts?.find((prompt) => prompt.type === 'text' && !prompt.multi_frame?.range)?.content || '';

  // 当 creation 变化时，重置提示显示状态
  useEffect(() => {
    setShowHevcMismatchTip(false);
    lastVideoErrorKeyRef.current = '';
  }, [creationId]);

  const handleVideoError = useMemoizedFn(async (errorInfo: VideoPlayerErrorInfo) => {
    const codec = settings?.codec || 'unknown';
    const isHevcCodec = codec === 'h265' || codec === 'h265_pro';
    const errorCode = errorInfo.error?.errorCode || 'unknown';
    const errorKey = `${errorInfo.src}::${errorCode}`;
    if (lastVideoErrorKeyRef.current === errorKey) {
      return;
    }
    lastVideoErrorKeyRef.current = errorKey;

    const isHevcError = isHevcPlaybackErrorCode(errorCode);

    // 如果是解码错误且视频源是 h265/hevc，则记录不支持
    if (isHevcCodec && isHevcError) {
      console.warn('HEVC decode error detected, marking as not supported');
      markHevcNotSupported();
      setShowHevcMismatchTip(true);
    } else {
      const isUrlValid = await checkUrlAvailable(errorInfo.src);
      // 未显示帮助提示，则上报错误
      // 把 errorCode / errorDetails 拼进 message，让 Sentry 按错误码分组（issue #34577 排查上下文）
      const videoErrCode = errorInfo.error.errorCode ?? 'unknown';
      const videoErrDetail = errorInfo.error.errorDetails || errorInfo.error.message || '';
      const videoErrCause = errorInfo.error.error instanceof Error ? errorInfo.error.error : undefined;
      Sentry.captureException(
        new Error(
          `Video play failed: [${videoErrCode}]${videoErrDetail ? ` ${videoErrDetail}` : ''}`,
          videoErrCause ? { cause: videoErrCause } : undefined,
        ),
        {
          tags: {
            'video.is_valid_url': isUrlValid,
            'video.is_hevc_supported': isHevcSupported(),
            'video.codec': codec,
            'video.is_hevc_error': isHevcError,
            'video.error_code': String(videoErrCode),
          },
          extra: {
            error: errorInfo.error,
          },
        },
      );
    }
  });

  const handleSelectedAction = (action: EllipsisActionTypeEnum) => {
    if (action === EllipsisActionTypeEnum.Delete) {
      openDeleteDialog({
        creationId,
        taskId,
        type: 'creation',
        creationType: creation?.type,
        reportParams,
        onDelete: () => {
          const remaining = (task.creations ?? []).filter((c) => c.id !== creationId);
          if (remaining.length > 0) {
            updateTask(taskId, { creations: remaining });
          } else {
            removeTask(taskId);
          }
        },
      });
      return;
    }

    if (action === EllipsisActionTypeEnum.Report) {
      onOpenActionDialog({
        action: EllipsisActionTypeEnum.Report,
        type: creationId ? 'creation' : 'task', // 如果有 creationId，则上报 creation 维度，否则上报 task 维度
        id: creationId || taskId,
        reportParams,
      });
      return;
    }

    // 其他按钮是生成物维度
    onOpenActionDialog({
      action,
      type: 'creation',
      id: creationId,
      reportParams,
    });
  };

  // 获取要显示的操作按钮列表
  const showActionList =
    creation?.type === 'image'
      ? getImageButtonConfig({ task, creation, isLgScreen, type: 'creation' })
      : getVideoButtonConfig({
          task,
          creation,
          isLgScreen,
          type: 'creation',
        });

  const renderStateCard = ({
    icon,
    title,
    content,
    className,
  }: {
    className?: string;
    icon?: React.ReactNode;
    title: string;
    content: React.ReactNode | string;
  }) => {
    return (
      <div
        className={cn(
          'flex h-full w-full flex-col items-center justify-center gap-2 px-5 py-8 text-center text-sm text-system-white64 lg:gap-1 lg:px-4 lg:text-xs',
          className,
        )}
      >
        {icon && <span className="shrink-0 text-2xl lg:text-xl">{icon}</span>}
        <p className="font-medium text-lg lg:text-base">{title}</p>
        <div className="w-full">{content}</div>
      </div>
    );
  };

  // 排队中或生成中
  const renderProgressState = () => {
    switch (taskState) {
      case 'queueing': {
        const queueConfig = getQueueI18nMessageConfig(
          queueWaitTime?.min[0] || 0,
          queueWaitTime?.max[0] || 0,
          isWaitTimeProlonged,
        );
        return (
          <div className="relative size-full overflow-hidden rounded-8 bg-system-black">
            <TaskStatusAnimation />
            <div
              className={cn(
                'absolute z-10 flex flex-col items-start gap-3',
                creationsLength > 1 ? 'right-6 bottom-6 left-6' : 'right-8 bottom-[26px] left-8',
                creationsLength === 1 && 'right-32',
              )}
            >
              <p className="font-medium text-lg text-system-white/70 leading-[26px]">{t('task.status.queue.title')}</p>

              {settings?.schedule_mode === 'nopeak' ? (
                <p className="text-left text-[#b3b6b8] text-sm leading-5">{t('task.status.queue.nopeak')}</p>
              ) : (
                <div className="text-left text-sm text-system-white/70 leading-[1.4]">
                  {queueWaitTime?.max[0] ? (
                    <p className="mb-1">
                      {t(
                        `task.status.queue.${queueConfig.key}`,
                        queueConfig.params
                          ? {
                              ...queueConfig.params,
                              unit: t(`task.status.queue.${queueConfig.params.unit}`),
                            }
                          : {},
                      )}
                    </p>
                  ) : null}
                  {userInfo?.subs_plan === 'free' ? (
                    <span className="inline-flex flex-wrap items-center gap-x-1">
                      {t.rich('task.status.queue.free', {
                        button: (chunks) => (
                          <button
                            type="button"
                            className="relative inline-flex cursor-pointer items-center focus-visible:outline-none"
                            onClick={() => {
                              openPlanDetailDialog({
                                fromPage: `${task.scene ? 'template -' : ''} queueing upgrade button`,
                              });
                              reportEvent('vip_price_modal_exposure', {
                                from_page: `${task.scene ? 'template -' : ''} queueing upgrade button`,
                              });
                              sendGAEvent('event', 'subscription_exposure', {
                                from_page: `${task.scene ? 'template -' : ''} queueing upgrade button`,
                              });
                            }}
                          >
                            <UpgradeGradientContent>{chunks}</UpgradeGradientContent>
                          </button>
                        ),
                      })}
                    </span>
                  ) : (
                    <span className="text-[#b3b6b8]">{t('task.status.queue.vip')}</span>
                  )}
                </div>
              )}
            </div>

            {creationsLength === 1 && (
              <CancelButton
                className="absolute right-8 bottom-8 z-10 h-auto min-w-0 rounded-none border-0 bg-transparent p-0 font-normal text-system-white/40 text-xs leading-[1.4] enabled:hover:bg-transparent enabled:hover:text-system-white/60 disabled:text-system-white/30 sm:hidden"
                taskId={taskId}
                onCancel={onCancelTask}
              />
            )}
          </div>
        );
      }
      case 'processing':
      case 'fetchingTaskDetail': {
        const estimatedTime = getEstimateTime(type, settings, task.scene, sceneFormParams);
        return renderStateCard({
          className: 'gap-4 px-[12%] lg:px-[10%]',
          title: t('task.status.creating.title'),
          content: (
            <CustomProgress
              hasFinished={taskState === 'fetchingTaskDetail'}
              id={task.id}
              allWaitTime={estimatedTime}
              stopProgress={95}
            />
          ),
        });
      }
      default:
        return null;
    }
  };

  const renderCreationResult = () => {
    // 任务已取消
    if (taskState === 'canceled') {
      return renderStateCard({
        title: t('task.status.canceled.title'),
        content: t('task.status.canceled.content'),
      });
    }

    if (taskState === 'failed') {
      const taskErrorCode = task.err_code;
      if (taskErrorCode === THIRD_PARTY_AUDIT_ILLEGAL_ERROR_CODE) {
        return renderStateCard({
          icon: <Fail />,
          title: t('task.status.thirdPartyAuditIllegal.title'),
          content: t('task.status.thirdPartyAuditIllegal.content'),
        });
      }

      if (isAuditFailedErrorCode(taskErrorCode)) {
        return renderStateCard({
          icon: <Fail />,
          title: t('task.status.auditVideoFailed.title'),
          content: t('task.status.auditVideoFailed.content'),
        });
      }

      return renderStateCard({
        icon: <Fail />,
        title: t('task.status.otherFailed.title'),
        content: t('task.status.otherFailed.content'),
      });
    }

    // task 为 success，但是生成物失败
    const errorCode = creation?.err_info?.err_code;
    if (errorCode) {
      if (errorCode === THIRD_PARTY_AUDIT_ILLEGAL_ERROR_CODE) {
        return renderStateCard({
          icon: <Fail />,
          title: t('task.status.thirdPartyAuditIllegal.title'),
          content: t('task.status.thirdPartyAuditIllegal.content'),
        });
      }

      // 国内会对存疑的生成物进行人工审核，所以添加个新状态
      if (SITE === 'china' && creation?.err_info?.err_code === 'Review') {
        return renderStateCard({
          icon: <BaseLineCreationCheck />,
          title: '审核中',
          content: '当前待审作品较多，请耐心等候～',
        });
      }

      if (isAuditFailedErrorCode(errorCode)) {
        return renderStateCard({
          icon: <Fail />,
          title: t('task.status.auditVideoFailed.title'),
          content: t('task.status.auditVideoFailed.content'),
        });
      }

      return renderStateCard({
        icon: <Fail />,
        title: t('task.status.otherFailed.title'),
        content: t('task.status.otherFailed.content'),
      });
    }

    // 如果生成物被删除
    if (!creation) {
      return renderStateCard({
        title: t('task.status.empty.title'),
        content: t('task.status.empty.content'),
      });
    }

    const {
      id,
      cover_uri,
      uri,
      nomark_uri: noMarkUri = '',
      src_video_duration: startTime,
      has_audio,
      type: creationType,
      resolution,
    } = creation;

    if (creationType === 'image') {
      const width = resolution?.width || 16;
      const height = resolution?.height || 9;

      return (
        <div className="flex h-full w-full cursor-pointer items-center justify-center overflow-hidden rounded-8">
          <MediaWithWatermark
            width={width}
            height={height}
            className="relative"
            showWatermark={SITE === 'china' || userInfo?.subs_plan === 'free'}
          >
            <ImageWithPlaceholder
              key={id}
              id={`creation_${id}`}
              src={cover_uri || noMarkUri || uri || ''}
              alt="creation image"
              loading="eager"
              className="h-full w-full bg-system-bg01 object-contain"
              placeHolderClassName="bg-system-bg01"
              onClick={() => {
                onOpenDetail({
                  ...creation,
                  task,
                });
              }}
            />
          </MediaWithWatermark>
        </div>
      );
    }

    return (
      <div className="relative h-full w-full">
        {isMobile ? (
          <ImageWithPlaceholder
            key={id}
            src={cover_uri || ''}
            alt="creation video preview"
            className="h-full w-full rounded-8 object-contain"
            onClick={() => {
              onOpenDetail({
                ...creation,
                task,
              });
            }}
          />
        ) : (
          <VideoPlayer
            src={getResourceUrl({ uri, noMarkUri })}
            key={id}
            poster={cover_uri}
            startTime={startTime}
            creationId={id}
            playOnHover={true}
            hoverTogglesControls={true}
            isLazy={true}
            hasAudio={has_audio}
            onError={handleVideoError}
            handleClickVideo={() => {
              onOpenDetail({
                ...creation,
                task,
              });
            }}
          />
        )}
        {/* 如果检测到 codec 不匹配错误，在右上角显示帮助提示按钮 */}
        {showHevcMismatchTip && creationType === 'video' && (
          <div className="absolute top-2 right-2 z-20 flex items-center gap-2">
            <CustomTooltip
              content={
                <div className="whitespace-pre-line text-sm leading-relaxed">
                  {t('task.status.hevcNotSupported.content')}
                  {locale === 'zh' && (
                    <>
                      {'\n'}
                      更多解决方案参考：{' '}
                      <a
                        href="https://shengshu.feishu.cn/wiki/TVMAwqiauigsXIkgmsrcFEO2nYf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-system-blue02 hover:underline"
                      >
                        不支持的音频/视频格式 解决方案
                      </a>
                    </>
                  )}
                </div>
              }
              contentClassName="max-w-[320px] p-4"
              side="bottom"
              sideOffset={8}
            >
              <Button className="items-center bg-system-black64 text-base text-system-white transition-colors hover:bg-system-black80">
                {t('task.status.hevcNotSupported.helpButton' as Parameters<typeof t>[0])}
                <BaseLineClose onClick={() => setShowHevcMismatchTip(false)} className="ml-3 text-base" />
              </Button>
            </CustomTooltip>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className="relative aspect-video max-w-[1080px] rounded-8 bg-system-black"
      onMouseEnter={() => throttleSetShowActions(true)}
      onMouseLeave={() => !dropdownOpen && throttleSetShowActions(false)}
    >
      {hasFinished ? (
        <>
          {renderCreationResult()}

          {/* 仅当批量生成成功或部分失败时显示 */}
          {creationsLength > 1 && creation && (
            <div
              className={cn(
                'absolute top-2 right-2 z-10 rounded-8 bg-black/50 backdrop-blur-xl',
                !isStarred && (isMobile || !showActions) && 'hidden',
              )}
            >
              <div className="flex h-full items-center">
                {
                  <CreationActionButtons
                    creation={creation}
                    task={task}
                    showActionList={isStarred && !showActions ? [ActionButtonType.STAR] : showActionList}
                    onSubmit={onSubmit}
                    onSelectedAction={handleSelectedAction}
                    onDropdownOpenChange={setDropdownOpen}
                  />
                }
              </div>
            </div>
          )}

          {creationsLength > 1 &&
            creation &&
            (creation.type === 'image' || creation.type === 'video' || showPostButton) &&
            !creation.err_info?.err_code && (
              <div
                className={cn(
                  'absolute top-2 left-2 z-10 flex items-center rounded-8 bg-black/50 backdrop-blur-xl',
                  (isMobile || !showActions) && 'hidden',
                )}
              >
                {creation.type === 'image' && (
                  <ImageApplyButton
                    className="rounded-8 p-1.5 text-inherit text-xl hover:bg-system-hover01"
                    noMarkUri={creation.nomark_uri || ''}
                    uri={creation.uri || ''}
                    onApplyToTool={(applyType) => {
                      reportEvent('apply_click', {
                        apply_type: applyType,
                        apply_position: 'create_task_card',
                      });
                    }}
                    backfillTextPrompt={continueCreationTextPrompt}
                    container={taskHistoryContainer}
                    creationId={creationId}
                    showFlyTrace
                    titleText={t('task.action.continueCreate')}
                  />
                )}
                {creation.type === 'video' && (
                  <VideoApplyButton
                    className="rounded-8 p-1.5 text-inherit text-xl hover:bg-system-hover01"
                    creation={creation}
                    container={taskHistoryContainer}
                    titleText={t('task.action.continueCreate')}
                  />
                )}
                {showPostButton && (
                  <CustomTooltip
                    content={creation.is_asset ? t('task.action.submitted') : t('task.action.submit')}
                    container={taskHistoryContainer}
                  >
                    <Button
                      variant="text"
                      size="original"
                      className="rounded-8 p-1.5 text-inherit text-xl hover:bg-system-hover01"
                      disabled={creation.is_asset}
                      onClick={() => onSubmit?.(creation)}
                    >
                      <BaseLineSubmit3
                        className={cn('text-system-white', creation.is_asset && 'text-system-white opacity-[.40]')}
                      />
                    </Button>
                  </CustomTooltip>
                )}
              </div>
            )}
        </>
      ) : (
        renderProgressState()
      )}
    </div>
  );
};
